import { apiClient } from './client';
import type { ApiResponse, PagedResult } from '@/types/api.types';
import type {
  ChequeRecordDto,
  ChequeRecordListDto,
  ChequeRecordFilterDto,
  CreateChequeRequest,
  UpdateChequeRequest,
  ChangeChequeStatusRequest,
} from '@/types/cheque.types';

/** A row in the cheque print queue (GAP-008). */
export interface ChequePrintQueueItem {
  id:                string;
  chequeNumber:      string;
  chequeDate:        string;
  bankAccountCode:   string;
  bankAccountName:   string;
  payeeName:         string;
  amount:            number;
  currencyCode:      string;
  referenceDocument: string | null;
  /** 0 = never printed (the queue's reason for being); higher = reprinted. */
  printCount:        number;
  lastPrintedDate:   string | null;
  /** GAP-007 — ACCOUNT_PAYEE, NOT_NEGOTIABLE, OPEN, or null for "not stated". */
  crossingType:      string | null;
}

/** One recorded print or reprint of a cheque. */
export interface ChequePrintLogEntry {
  id:            string;
  printSequence: number;
  isReprint:     boolean;
  reprintReason: string | null;
  remarks:       string | null;
  printedBy:     string | null;
  printedByName?: string | null;
  printedDate:   string;
}

export interface ChequePrintData {
  // Cheque data
  chequeNumber: string;
  payeeName: string;
  amount: number;
  amountInWords: string;
  chequeDate: string;
  accountNumber: string;
  bankName: string;
  branchName: string;
  currencyCode: string;
  // Page dimensions (mm)
  pageWidthMm: number;
  pageHeightMm: number;
  // Payee name field
  payeeNameX: number;
  payeeNameY: number;
  payeeNameWidth: number;
  // Amount in figures
  amountFiguresX: number;
  amountFiguresY: number;
  amountFiguresWidth: number;
  // Amount in words
  amountWordsX: number;
  amountWordsY: number;
  amountWordsWidth: number;
  // Cheque date
  chequeDateX: number;
  chequeDateY: number;
  // Cheque number
  chequeNoX: number;
  chequeNoY: number;
  // Font config
  defaultFontName: string;
  defaultFontSizePt: number;
  amountFontSizePt: number;

  /** Crossing wording and where it prints — null when the cheque is open or uncrossed (GAP-006). */
  crossingText:  string | null;
  crossingTextX: number | null;
  crossingTextY: number | null;

  /**
   * The company authority block. Null when the layout has none, or the operator suppressed it for
   * this run because the stationery is stamped by hand (GAP-007).
   */
  rubberStampText: string | null;
  rubberStampX:    number | null;
  rubberStampY:    number | null;

  /** Set when the payee on this print is not the cheque's recorded payee — the audited override. */
  payeeNameOverride: string | null;
}

/** One cheque in a batch print run (GAP-007). */
export interface BatchPrintCheque {
  chequeId:  string;
  printData: ChequePrintData;
}

/** A cheque that was selected but cannot be printed, and why. */
export interface BatchPrintSkipped {
  chequeId:     string;
  chequeNumber: string;
  reason:       string;
}

export interface BatchPrintResult {
  batchId: string;
  cheques: BatchPrintCheque[];
  skipped: BatchPrintSkipped[];
}

export interface BatchPrintConfirmResult {
  batchId:        string;
  confirmedCount: number;
  skipped:        BatchPrintSkipped[];
}

export interface BatchPrintRequest {
  chequeIds:          string[];
  printRubberStamp:   boolean;
  chequeLayoutId?:    string | null;
  /** Requires OVERRIDE_CASH_PAYEE_BNK_CHEQUE. Omit for a normal run. */
  cashPayeeOverride?: string | null;
  /**
   * GAP-007 — crossing to print on every cheque in the run. Requires
   * OVERRIDE_CROSSING_BNK_CHEQUE. Omit to print each cheque as it was registered.
   */
  crossingOverride?:  string | null;
}

export const chequeApi = {
  /** GET /api/v1/cheques — paginated list */
  getList: (filter?: ChequeRecordFilterDto) =>
    apiClient.get<ApiResponse<PagedResult<ChequeRecordListDto>>>('/cheques', { params: filter }),

  /**
   * GET /api/v1/cheques — received cheques still in hand and eligible to deposit into this bank
   * account (GAP-016). type Received, status Received, and either already earmarked for this bank
   * or not earmarked for any bank yet (GAP-013 — most received cheques choose no bank at receipt
   * time, and are eligible for any deposit, not excluded by a bank match they don't have).
   */
  /** Received cheques of one document (e.g. a customer receipt) still in hand, i.e. not yet deposited (424365). */
  inHandForReference: async (referenceDocument: string) => {
    const r = await apiClient.get<ApiResponse<PagedResult<ChequeRecordListDto>>>('/cheques',
      { params: { chequeType: 'Received', status: 'Received', search: referenceDocument, pageSize: 100 } });
    return (r.data?.data?.items ?? []).filter(c => c.referenceDocument === referenceDocument);
  },

  inHandForDeposit: (bankAccountId: string) =>
    apiClient.get<ApiResponse<PagedResult<ChequeRecordListDto>>>('/cheques',
      { params: {
          bankAccountId, includeUnassignedBankAccount: true,
          chequeType: 'Received', status: 'Received', pageSize: 200,
        } }),

  /**
   * GET /api/v1/cheques — received cheques still in hand, for endorsing to a supplier (ML-12).
   *
   * Unlike the deposit picker this names no bank account: the cheque is being handed to a
   * supplier rather than paid into anything of ours, so which bank it might once have been
   * earmarked for does not narrow the choice.
   */
  inHandForEndorsement: () =>
    apiClient.get<ApiResponse<PagedResult<ChequeRecordListDto>>>('/cheques',
      { params: { chequeType: 'Received', status: 'Received', pageSize: 200 } }),

  /** GET /api/v1/cheques/{id} — full cheque record detail */
  getById: (id: string) =>
    apiClient.get<ApiResponse<ChequeRecordDto>>(`/cheques/${id}`),

  /** POST /api/v1/cheques — create new cheque record */
  create: (data: CreateChequeRequest) =>
    apiClient.post<ApiResponse<{ id: string }>>('/cheques', data),

  /** PUT /api/v1/cheques/{id} — update cheque record */
  update: (id: string, data: UpdateChequeRequest) =>
    apiClient.put<ApiResponse<object>>(`/cheques/${id}`, data),

  /** POST /api/v1/cheques/{id}/change-status — change cheque status */
  changeStatus: (id: string, data: ChangeChequeStatusRequest) =>
    apiClient.post<ApiResponse<object>>(`/cheques/${id}/change-status`, data),

  /** DELETE /api/v1/cheques/{id} — soft-delete (draft only) */
  delete: (id: string) =>
    apiClient.delete<ApiResponse<object>>(`/cheques/${id}`),

  /** POST /api/v1/cheques/{id}/print-data — get merged print data + layout coordinates */
  printData: (id: string, chequeLayoutId?: string) =>
    apiClient.post<ApiResponse<ChequePrintData>>(`/cheques/${id}/print-data`, { chequeLayoutId: chequeLayoutId ?? null }),

  // ── Print queue & confirmation (GAP-008) ─────────────────────────────────

  /** GET /api/v1/cheques/print-queue — issued cheques and their print state */
  printQueue: (params: { bankAccountId?: string; includePrinted?: boolean; page?: number; pageSize?: number }) =>
    apiClient.get<ApiResponse<PagedResult<ChequePrintQueueItem>>>('/cheques/print-queue', { params }),

  /** GET /api/v1/cheques/{id}/print-history — every print and reprint, in order */
  printHistory: (id: string) =>
    apiClient.get<ApiResponse<ChequePrintLogEntry[]>>(`/cheques/${id}/print-history`),

  /** POST /api/v1/cheques/{id}/confirm-print — record the routine first print */
  confirmPrint: (id: string, remarks?: string) =>
    apiClient.post<ApiResponse<object>>(`/cheques/${id}/confirm-print`, { remarks: remarks ?? null }),

  /**
   * POST /api/v1/cheques/{id}/reprint — record a reprint; a reason is required, because it means
   * a second physical cheque could carry this number.
   */
  reprint: (id: string, reason: string, remarks?: string) =>
    apiClient.post<ApiResponse<object>>(`/cheques/${id}/reprint`, { reason, remarks: remarks ?? null }),

  // ── Batch printing (GAP-007) ─────────────────────────────────────────────

  /**
   * POST /api/v1/cheques/batch-print — render a selection as one run.
   *
   * Rendering only: nothing counts as printed until batchConfirmPrint. Cheques that cannot be
   * printed come back in `skipped` with a reason rather than failing the whole run.
   */
  batchPrint: (request: BatchPrintRequest) =>
    apiClient.post<ApiResponse<BatchPrintResult>>('/cheques/batch-print', request),

  /** POST /api/v1/cheques/batch-confirm-print — record that the run reached paper. */
  batchConfirmPrint: (request: BatchPrintRequest & { batchId?: string | null; remarks?: string | null }) =>
    apiClient.post<ApiResponse<BatchPrintConfirmResult>>('/cheques/batch-confirm-print', request),

  /**
   * POST /api/v1/cheques/{id}/represent — re-present a representable returned cheque. It goes back
   * to the bank and its receipt accounting is re-applied (GAP-018, Q1).
   */
  represent: (id: string, representDate: string, remarks?: string) =>
    apiClient.post<ApiResponse<object>>(`/cheques/${id}/represent`, { representDate, remarks: remarks ?? null }),
};
